import React, { useState, useEffect } from 'react';
import axios from 'axios';

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [records, setRecords] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [newRecord, setNewRecord] = useState('');
  const [editRecordId, setEditRecordId] = useState(null);
  const [editRecordData, setEditRecordData] = useState('');

  const backendUrl = process.env.REACT_APP_VERCEL_URL || 'http://localhost:3000';
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (token) {
      console.log('Token found on load:', token);
      fetchRecords();
    }
  }, []);

  const fetchRecords = async () => {
    const fetchToken = localStorage.getItem('token');
    console.log('Fetching records with token:', fetchToken);
    try {
      const res = await axios.get(`${backendUrl}/records`, {
        headers: { Authorization: `Bearer ${fetchToken}` },
      });
      console.log('Records response:', res.data);
      setRecords(res.data);
      setLoggedIn(true);
    } catch (err) {
      console.error('Fetch records failed:', err.response?.status, err.response?.data);
      localStorage.removeItem('token');
      setLoggedIn(false);
    }
  };

  const handleLogin = async () => {
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) {
      setError('Email and password are required');
      return;
    }
    const payload = { email: trimmedEmail, password: trimmedPassword };
    console.log('Sending login request:', JSON.stringify(payload));
    try {
      const res = await axios.post(`${backendUrl}/login`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('Login response:', res.data);
      if (res.data.session) {
        const token = res.data.session.access_token;
        console.log('Storing token:', token);
        localStorage.setItem('token', token);
        setLoggedIn(true);
        await fetchRecords();
      }
    } catch (err) {
      console.error('Login failed:', err.response?.status, err.response?.data);
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setLoggedIn(false);
    setRecords([]);
    setEmail('');
    setPassword('');
  };

  const handleAddRecord = async () => {
    if (!newRecord) return;
    try {
      const res = await axios.post(
        `${backendUrl}/records`,
        { data: newRecord },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecords([...records, res.data]);
      setNewRecord('');
    } catch (err) {
      console.error('Add record failed:', err);
      setError('Failed to add record');
    }
  };

  const handleEditRecord = (record) => {
    setEditRecordId(record.id);
    setEditRecordData(record.data);
  };

  const handleUpdateRecord = async () => {
    if (!editRecordData) return;
    try {
      const res = await axios.put(
        `${backendUrl}/records/${editRecordId}`,
        { data: editRecordData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecords(records.map((r) => (r.id === editRecordId ? res.data : r)));
      setEditRecordId(null);
      setEditRecordData('');
    } catch (err) {
      console.error('Update record failed:', err);
      setError('Failed to update record');
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      await axios.delete(`${backendUrl}/records/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(records.filter((r) => r.id !== id));
    } catch (err) {
      console.error('Delete record failed:', err);
      setError('Failed to delete record');
    }
  };

  return (
    <div className="p-5 max-w-4xl mx-auto">
      {!loggedIn ? (
        <div className="space-y-4">
          <h1 className="text-2xl font-bold">Login</h1>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="block w-full p-2 border rounded"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="block w-full p-2 border rounded"
          />
          <button onClick={handleLogin} className="bg-blue-500 text-white p-2 rounded w-full">
            Login
          </button>
          {error && <p className="text-red-500">{error}</p>}
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold mb-4">Records</h1>
          <div className="mb-4">
            <input
              type="text"
              value={newRecord}
              onChange={(e) => setNewRecord(e.target.value)}
              placeholder="Add new record"
              className="p-2 border rounded mr-2"
            />
            <button onClick={handleAddRecord} className="bg-green-500 text-white p-2 rounded">
              Add
            </button>
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border p-2">ID</th>
                <th className="border p-2">Data</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td className="border p-2">{record.id}</td>
                  <td className="border p-2">
                    {editRecordId === record.id ? (
                      <input
                        type="text"
                        value={editRecordData}
                        onChange={(e) => setEditRecordData(e.target.value)}
                        className="p-1 border rounded w-full"
                      />
                    ) : (
                      record.data
                    )}
                  </td>
                  <td className="border p-2 flex space-x-2">
                    {editRecordId === record.id ? (
                      <button onClick={handleUpdateRecord} className="bg-blue-500 text-white p-1 rounded">
                        Save
                      </button>
                    ) : (
                      <button onClick={() => handleEditRecord(record)} className="bg-yellow-500 text-white p-1 rounded">
                        Edit
                      </button>
                    )}
                    <button onClick={() => handleDeleteRecord(record.id)} className="bg-red-500 text-white p-1 rounded">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleLogout} className="mt-4 bg-red-500 text-white p-2 rounded w-full">
            Logout
          </button>
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
}

export default App;